/**
 * Maestro de CLIENTES (facturas emitidas). Espejo de `supplierMaster.ts`:
 * cada cliente se identifica por NIF dentro de una empresa emisora y aporta
 * la cuenta de cliente (430…), la cuenta de venta (700…) y el código postal
 * (necesario para el modelo 347).
 */
import { supabase } from "@/integrations/supabase/client";
import { normalizeOperationType, type SupplierOperationType } from "@/lib/supplierMaster";

export type CustomerOperationType = SupplierOperationType;

export interface CustomerMasterEntry {
  id?: string;
  accountCode: string;
  salesAccount: string;
  description: string;
  operationType: CustomerOperationType;
  postalCode?: string;
}

const TABLE = "customer_master" as const;
type SupabaseAny = ReturnType<typeof supabase.from>;
const table = () => (supabase.from(TABLE as unknown as never) as unknown) as SupabaseAny;

export const normalizeNif = (nif: string): string =>
  (nif || "")
    .normalize("NFKC")
    .replace(/[^A-Z0-9]/gi, "")
    .toUpperCase()
    .replace(/^ES(?=[A-Z0-9]{9}$)/, "");

interface CustomerRow {
  id: string;
  account_code: string | null;
  sales_account: string | null;
  description: string | null;
  tax_id: string | null;
  operation_type: string | null;
  postal_code: string | null;
}

/** Carga el maestro de clientes de una empresa indexado por NIF normalizado. */
export const loadCustomerMaster = async (
  companyId?: string,
): Promise<Map<string, CustomerMasterEntry>> => {
  const map = new Map<string, CustomerMasterEntry>();
  if (!companyId) return map;

  const PAGE = 1000;
  let from = 0;
  const all: CustomerRow[] = [];

  while (true) {
    const { data, error } = await table()
      .select("id, account_code, sales_account, description, tax_id, operation_type, postal_code")
      .eq("company_id", companyId)
      .range(from, from + PAGE - 1);
    if (error) {
      console.error("Error loading customer master:", error);
      return map;
    }
    const rows = (data || []) as unknown as CustomerRow[];
    if (rows.length === 0) break;
    all.push(...rows);
    if (rows.length < PAGE) break;
    from += PAGE;
  }

  for (const row of all) {
    const nif = normalizeNif(row.tax_id || "");
    if (!nif) continue;
    map.set(nif, {
      id: row.id,
      accountCode: row.account_code || "",
      salesAccount: row.sales_account || "",
      description: row.description || "",
      operationType: normalizeOperationType(row.operation_type),
      postalCode: (row.postal_code || "").replace(/\D/g, "").slice(0, 5) || undefined,
    });
  }
  return map;
};

export const lookupCustomer = (
  master: Map<string, CustomerMasterEntry>,
  taxId: string,
): CustomerMasterEntry | undefined => {
  if (!taxId) return undefined;
  return master.get(normalizeNif(taxId));
};

export interface CustomerMasterRecord extends CustomerMasterEntry {
  taxId: string;
}

/** Lista completa (para el maestro editable). */
export const listCustomers = async (companyId: string): Promise<(CustomerMasterRecord & { id: string })[]> => {
  const { data, error } = await table()
    .select("id, account_code, sales_account, description, tax_id, operation_type, postal_code")
    .eq("company_id", companyId)
    .order("description");
  if (error) {
    console.error("Error listing customers:", error);
    return [];
  }
  return ((data || []) as unknown as CustomerRow[]).map((row) => ({
    id: row.id,
    taxId: row.tax_id || "",
    accountCode: row.account_code || "",
    salesAccount: row.sales_account || "",
    description: row.description || "",
    operationType: normalizeOperationType(row.operation_type),
    postalCode: (row.postal_code || "").replace(/\D/g, "").slice(0, 5) || undefined,
  }));
};

export const upsertCustomer = async (
  companyId: string,
  entry: CustomerMasterRecord,
): Promise<{ error?: string }> => {
  const payload = {
    company_id: companyId,
    account_code: entry.accountCode.trim(),
    sales_account: entry.salesAccount.trim(),
    description: entry.description.trim(),
    tax_id: normalizeNif(entry.taxId),
    operation_type: entry.operationType,
    postal_code: (entry.postalCode || "").replace(/\D/g, "").slice(0, 5) || null,
  };

  if (entry.id) {
    const { error } = await table().update(payload).eq("id", entry.id);
    return { error: error?.message };
  }

  // Evita duplicados por NIF dentro de la misma empresa.
  const { data: existing } = await table()
    .select("id")
    .eq("company_id", companyId)
    .eq("tax_id", payload.tax_id)
    .maybeSingle();
  const existingId = (existing as { id?: string } | null)?.id;
  if (existingId) {
    const { error } = await table().update(payload).eq("id", existingId);
    return { error: error?.message };
  }

  const { error } = await table().insert(payload);
  return { error: error?.message };
};

export const deleteCustomer = async (id: string): Promise<{ error?: string }> => {
  const { error } = await table().delete().eq("id", id);
  return { error: error?.message };
};
